/**
 * Filter state + (de)serialization. Pure and DOM-free so it can be unit-tested
 * and reused for URL state. The panel DOM wiring lives in main.ts.
 */

export const ALL_OUTCOMES = ["APPROVED", "REFUSED", "OTHER"] as const;
export type Outcome = (typeof ALL_OUTCOMES)[number];

export interface FilterState {
  types: string[]; // appl_type values; empty = all
  outcomes: Outcome[]; // empty = none shown; default = all three
  from: string; // yyyy-mm-dd (inclusive)
  to: string; // yyyy-mm-dd (inclusive)
  district: string; // "" = all, else D-code
  street: string; // free text (address contains)
  keyword: string; // free text over description/type/address
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Format a Date as yyyy-mm-dd in local time. */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO `yyyy-mm-dd` -> display `dd/mm/yyyy` (returns the input unchanged if not ISO). */
export function isoToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Display `dd/mm/yyyy` -> ISO `yyyy-mm-dd`, or null if the string is not a real
 * calendar date (rejects e.g. 31/02/2026 and malformed input).
 */
export function displayToIso(value: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return `${yyyy}-${mm}-${dd}`;
}

/** Default decision-date window: the last 12 months up to `today`. */
export function defaultDateRange(today: Date): { from: string; to: string } {
  const from = new Date(today);
  from.setFullYear(from.getFullYear() - 1);
  return { from: isoDate(from), to: isoDate(today) };
}

export function defaultFilters(today: Date): FilterState {
  const { from, to } = defaultDateRange(today);
  return { types: [], outcomes: [...ALL_OUTCOMES], from, to, district: "", street: "", keyword: "" };
}

function isOutcome(v: string): v is Outcome {
  return (ALL_OUTCOMES as readonly string[]).includes(v);
}

/** Serialize filters into URL search params (only non-empty values). */
export function filtersToSearchParams(f: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (f.types.length > 0) p.set("types", f.types.join(","));
  // Always encode outcomes so an intentionally-narrowed set round-trips.
  p.set("outcomes", f.outcomes.join(","));
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.district) p.set("district", f.district);
  if (f.street) p.set("street", f.street);
  if (f.keyword) p.set("keyword", f.keyword);
  return p;
}

/** Parse filters from URL search params, falling back to defaults for `today`. */
export function searchParamsToFilters(p: URLSearchParams, today: Date): FilterState {
  const base = defaultFilters(today);
  const types = p.get("types");
  const outcomesRaw = p.get("outcomes");
  const from = p.get("from");
  const to = p.get("to");
  const district = p.get("district");
  const street = p.get("street");
  const keyword = p.get("keyword");

  return {
    types: types ? types.split(",").map((s) => s.trim()).filter(Boolean) : base.types,
    outcomes:
      outcomesRaw !== null
        ? outcomesRaw.split(",").map((s) => s.trim()).filter(isOutcome)
        : base.outcomes,
    from: from && /^\d{4}-\d{2}-\d{2}$/.test(from) ? from : base.from,
    to: to && /^\d{4}-\d{2}-\d{2}$/.test(to) ? to : base.to,
    district: district ?? base.district,
    street: street ?? base.street,
    keyword: keyword ?? base.keyword,
  };
}

/** Trailing debounce for viewport refetch + autosuggest. */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): (...args: A) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: A): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}
